import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close, HeartStraight } from '@liveauctioneers/hammer-ui-core/icons';
import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useAtom, useSetAtom } from 'jotai';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { activePromptAtom, isPromptVisibleAtom, lastPromptClosedAtAtom } from '@/atoms/exitIntentSavePromptAtoms';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useExitIntentSavePromptController } from '@/hooks/useExitIntentSavePromptController';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { toggleItemSave } from '@/redux/modules/item/saved/savedItems.actions';
import { CompleteAccountModalContext } from '@/redux/modules/modal';
import { ButtonLocation, useTrackExitIntentPromptViewedAnalytics } from '@/utils/amplitude/analytics';
import {
    AUTO_DISMISS_DESKTOP_MS,
    AUTO_DISMISS_MOBILE_MS,
    CONFIRM_TOAST_MS,
} from '@/utils/exitIntentSavePrompt/exitIntentSavePrompt.constants';
import { UnsavedViewRecord } from '@/utils/exitIntentSavePrompt/exitIntentSavePrompt.types';
import {
    addShownLot,
    incrementSessionPromptCount,
    setUserDismissed,
} from '@/utils/exitIntentSavePrompt/exitIntentSavePromptStorage';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

const EXIT_MS = 200;

const getPrefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

enum ClosePromptReason {
    AutoDismiss = 'auto-dismiss',
    Dismiss = 'dismiss',
    Save = 'save',
}

enum Phase {
    Entering = 'entering',
    Exiting = 'exiting',
    Idle = 'idle',
    Visible = 'visible',
}

/**
 * App-level, always-mounted prompt for the exit-intent save nudge (LAAC-2038 spec Sections 4, 7, 9, 10).
 * Reads `activePromptAtom` (set by `useExitIntentSavePromptController`); renders nothing when null.
 * Owns its own entrance/exit animation, the silent hover/focus-pausable auto-dismiss timer, and the
 * "shown" bookkeeping (analytics + per-lot/counter writes + the exclusivity lock) fired exactly once
 * per rendered prompt.
 *
 * Logged-out save uses `useRequireLogin` so the deferred callback lives on this always-mounted
 * component after the visual prompt closes.
 */
export const ExitIntentSavePrompt = () => {
    useExitIntentSavePromptController();

    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isMobile = useIsMobileLayout();
    const verifyLogin = useRequireLogin();

    const [activePrompt, setActivePrompt] = useAtom(activePromptAtom);
    const setIsPromptVisible = useSetAtom(isPromptVisibleAtom);
    const setLastPromptClosedAt = useSetAtom(lastPromptClosedAtAtom);
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const trackExitIntentPromptViewed = useTrackExitIntentPromptViewedAnalytics();

    const [renderedRecord, setRenderedRecord] = useState(activePrompt);
    const [phase, setPhase] = useState<Phase>(Phase.Idle);

    const hasFiredViewedRef = useRef(false);
    const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const remainingMsRef = useRef(0);
    const pauseStartRef = useRef<number | null>(null);

    const clearAutoDismissTimer = useCallback(() => {
        if (autoDismissTimerRef.current !== null) {
            clearTimeout(autoDismissTimerRef.current);
            autoDismissTimerRef.current = null;
        }
    }, []);

    const closePrompt = useCallback(
        (reason: ClosePromptReason) => {
            clearAutoDismissTimer();
            if (reason === 'dismiss') {
                setUserDismissed();
            }
            setIsPromptVisible(false);
            setLastPromptClosedAt(Date.now());
            setActivePrompt(null);
        },
        [clearAutoDismissTimer, setActivePrompt, setIsPromptVisible, setLastPromptClosedAt]
    );

    const startAutoDismissTimer = useCallback(
        (durationMs: number) => {
            clearAutoDismissTimer();
            pauseStartRef.current = Date.now();
            autoDismissTimerRef.current = setTimeout(() => {
                closePrompt(ClosePromptReason.AutoDismiss);
            }, durationMs);
        },
        [clearAutoDismissTimer, closePrompt]
    );

    const pauseAutoDismissTimer = useCallback(() => {
        if (pauseStartRef.current === null) {
            return;
        }
        remainingMsRef.current = Math.max(0, remainingMsRef.current - (Date.now() - pauseStartRef.current));
        pauseStartRef.current = null;
        clearAutoDismissTimer();
    }, [clearAutoDismissTimer]);

    const resumeAutoDismissTimer = useCallback(() => {
        if (phase !== Phase.Visible || pauseStartRef.current !== null) {
            return;
        }
        startAutoDismissTimer(remainingMsRef.current);
    }, [phase, startAutoDismissTimer]);

    // Mirror the atom into local state so the exit animation can play before the record is gone.
    useEffect(() => {
        if (activePrompt) {
            setRenderedRecord(activePrompt);
            setPhase(Phase.Entering);
            hasFiredViewedRef.current = false;
        } else if (renderedRecord) {
            setPhase(Phase.Exiting);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePrompt]);

    useEffect(() => {
        if (phase !== Phase.Entering) {
            return;
        }

        const raf = requestAnimationFrame(() => {
            setPhase(Phase.Visible);
        });
        return () => {
            cancelAnimationFrame(raf);
        };
    }, [phase]);

    useEffect(() => {
        if (phase !== Phase.Exiting) {
            return;
        }

        const prefersReducedMotion = getPrefersReducedMotion();
        const timeout = setTimeout(
            () => {
                setRenderedRecord(null);
                setPhase(Phase.Idle);
            },
            prefersReducedMotion ? 0 : EXIT_MS
        );
        return () => {
            clearTimeout(timeout);
        };
    }, [phase]);

    // Fire the "shown" bookkeeping exactly once, at the moment the prompt actually renders (LAAC-2038 AC 21)
    useEffect(() => {
        if (phase !== Phase.Visible || !renderedRecord || hasFiredViewedRef.current) {
            return;
        }
        hasFiredViewedRef.current = true;

        addShownLot(renderedRecord.itemId);
        incrementSessionPromptCount();
        setIsPromptVisible(true);
        trackExitIntentPromptViewed({
            catalogId: renderedRecord.catalogId,
            itemId: renderedRecord.itemId,
            lotTitle: renderedRecord.lotTitle,
            platform: isMobile ? 'mobileWeb' : 'desktop',
        });

        const durationMs = isMobile ? AUTO_DISMISS_MOBILE_MS : AUTO_DISMISS_DESKTOP_MS;
        remainingMsRef.current = durationMs;
        startAutoDismissTimer(durationMs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase, renderedRecord]);

    useEffect(() => clearAutoDismissTimer, [clearAutoDismissTimer]);

    const onSave = useCallback(() => {
        if (!renderedRecord) {
            return;
        }

        // Capture before close — useRequireLogin stores this callback on the always-mounted
        // component, so it still fires after the visual prompt is gone.
        const record: UnsavedViewRecord = renderedRecord;
        const saveLocation = location;

        verifyLogin({
            forceSignUp: !userLoggedIn,
            modalContext: {
                eventContext: EventContext.saveItem,
                id: record.itemId,
                itemId: record.itemId,
                type: CompleteAccountModalContext.SeeSoldPrice,
            },
            requireLogin: true,
            runFunction: () => {
                dispatch(
                    toggleItemSave({
                        buttonLocation: ButtonLocation.ExitIntentPrompt,
                        canHaveEasyShippingTag: false,
                        catalogId: record.catalogId,
                        itemId: record.itemId,
                        location: saveLocation,
                    })
                );
                toast(
                    'success',
                    {
                        description: formatMessage(['exitIntentSavePrompt', 'toast', 'description']),
                        title: formatMessage(['exitIntentSavePrompt', 'toast', 'title']) as unknown as string,
                    },
                    {
                        autoClose: CONFIRM_TOAST_MS,
                        position: isMobile ? 'top-center' : 'top-right',
                    }
                );
            },
        });
        closePrompt(ClosePromptReason.Save);
    }, [closePrompt, dispatch, formatMessage, isMobile, location, renderedRecord, userLoggedIn, verifyLogin]);

    const onDismiss = useCallback(() => {
        closePrompt(ClosePromptReason.Dismiss);
    }, [closePrompt]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Escape') {
                onDismiss();
            }
        },
        [onDismiss]
    );

    if (!renderedRecord || phase === Phase.Idle) {
        return null;
    }

    const prefersReducedMotion = getPrefersReducedMotion();
    const isVisible = phase === Phase.Visible;
    const positionClasses = isMobile ? 'inset-x-sm bottom-sm' : 'bottom-lg right-lg w-[min(412px,calc(100vw-32px))]';
    const hiddenClasses = isMobile ? 'translate-y-full opacity-0' : 'translate-y-3 opacity-0';
    const visibilityClasses = isVisible ? 'translate-y-0 opacity-100' : hiddenClasses;

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div
            aria-live="polite"
            className={cn(
                // Portal escapes ancestor stacking contexts; z-index still needed vs other
                // body-level overlays (sticky headers / SMS popover at 1000, modals ~10400).
                'fixed z-[1000] rounded-sm bg-background-secondary shadow-03',
                // Literal duration class required for Tailwind's scanner (matches EXIT_MS / entrance timing).
                !prefersReducedMotion && 'transition-[transform,opacity] duration-[240ms] ease-out',
                positionClasses,
                visibilityClasses
            )}
            onBlur={resumeAutoDismissTimer}
            onFocus={pauseAutoDismissTimer}
            onKeyDown={onKeyDown}
            onMouseEnter={pauseAutoDismissTimer}
            onMouseLeave={resumeAutoDismissTimer}
            role="status"
        >
            <div className="relative flex items-start py-xs pl-xs">
                <div className="relative size-[116px] shrink-0 overflow-hidden rounded-sm border border-border-secondary bg-background-primary">
                    <img
                        alt={renderedRecord.lotTitle}
                        className="size-full object-contain"
                        src={renderedRecord.lotThumbnailUrl}
                    />
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-sm px-sm py-xs pr-lg">
                    <H5>{formatMessage(['exitIntentSavePrompt', 'title'])}</H5>
                    <Button
                        className="w-full"
                        iconLeft={HeartStraight}
                        onClick={onSave}
                        size="sm"
                        style="primary"
                        variant="filled"
                    >
                        {formatMessage(['exitIntentSavePrompt', 'saveButton'])}
                    </Button>
                </div>
                <IconButton
                    aria-label={formatMessage(['exitIntentSavePrompt', 'dismissLabel']) as unknown as string}
                    className="absolute right-xs top-0"
                    icon={Close}
                    onClick={onDismiss}
                    size="sm"
                    style="neutral"
                    variant="transparent"
                />
            </div>
        </div>,
        document.body
    );
};
